//@flow
import * as React from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { compose } from 'recompose';
import history from 'common/middleware/history';
import settingsIds from 'common/tests/core/settingsIds.json';
import {
    personnelClientIdSelector,
    clientIdSelector,
    personnelDataSelector,
    sessionSelector
} from 'common/redux/selectors/sessionSelector';
import { Session } from 'common/models/internal/Session';
import WriteAccessActions from 'common/redux/actions/writeAccess';
import UserMenuIcon from './UserMenuIcon';
import Icon from 'common/components/core/icon';

import theme from 'common/components/core/menu/UserMenuItem.scss';
import { Column, Columns } from '@locus-taxy/neo-ui';
import {
    withGlobalTimezone,
    type GlobalTimezonePropType
} from 'common/modules/timezone';
import UserRole from './UserRole';

type Props = {
    ...GlobalTimezonePropType,
    clientId: string,
    personnelClientId: string,
    userName: string,
    session: ?Session,
    onSignout: Function,
    requestWriteAccess: Function,
    t: Object
};

class UserMenuItem extends React.PureComponent<Props> {
    requestWriteAccess = (): void => {
        const { requestWriteAccess, clientId } = this.props;
        requestWriteAccess({ clientId });
    };

    openSettingsPanel = (): void => {
        const { clientId } = this.props;

        history.push({
            pathname: `/client/${clientId}/mysettings`
        });
    };

    settingsHasErrors = () => {
        const { session } = this.props;
        return (
            session &&
            (session.isPasswordExpiringInNext7Days() ||
                session.isPasswordExpired())
        );
    };

    render() {
        const {
            userName,
            personnelClientId,
            onSignout,
            globalTimezoneData,
            t
        } = this.props;
        const userNameClasses = classnames(
            theme.listItem,
            theme.listGroup,
            theme.header,
            theme.borderBottom
        );
        const notificationClasses = classnames(
            theme.listItem,
            theme.link,
            theme.borderBottom
        );
        const signoutClasses = classnames(
            theme.listItem,
            theme.link,
            theme.borderBottom
        );

        return (
            <div
                className={theme.item}
                data-test-id={settingsIds.personnelMenu.accountIcon}
            >
                <UserMenuIcon />

                <ul className={theme.verticalMenu} tabIndex="-1">
                    <li className={userNameClasses}>
                        <div className={theme.title}>{userName}</div>
                        <Columns>
                            <Column>
                                <div className={theme.description}>
                                    {personnelClientId}
                                </div>
                            </Column>
                            <Column width="content">
                                <div className={theme.description}>
                                    {t('timezone_abbreviation')}:{' '}
                                    {globalTimezoneData.abbreviation}
                                </div>
                            </Column>
                        </Columns>
                        <UserRole />
                    </li>

                    {personnelClientId === 'mara' ? (
                        <li
                            className={notificationClasses}
                            onClick={this.requestWriteAccess}
                        >
                            {t('requestWriteAccess')}
                        </li>
                    ) : null}

                    <li
                        className={classnames(
                            notificationClasses,
                            this.settingsHasErrors() ? theme.error : null
                        )}
                        onClick={this.openSettingsPanel}
                        data-test-id={settingsIds.personnelMenu.mySettings}
                    >
                        <span>{t('mySettings')}</span>
                        {this.settingsHasErrors() ? (
                            <Icon
                                icon="alert"
                                circular
                                className={theme.alertIcon}
                            />
                        ) : null}
                    </li>

                    <li
                        className={signoutClasses}
                        onClick={onSignout}
                        data-test-id={settingsIds.personnelMenu.signOut}
                    >
                        <span>{t('signOut')}</span>
                    </li>
                </ul>
            </div>
        );
    }
}

const mapStateToProps = (state, props) => {
    const personnelClientId = personnelClientIdSelector(state);
    const clientId = clientIdSelector(state, props);
    const { name } = personnelDataSelector(state);
    const session = sessionSelector(state);
    return {
        personnelClientId,
        clientId,
        userName: name,
        session
    };
};

export default compose(
    translate('settings'),
    withGlobalTimezone,
    connect(mapStateToProps, {
        requestWriteAccess: WriteAccessActions.requestWriteAccess
    })
)(UserMenuItem);
